import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { demo, money, SyntheticTag, LiveTag } from "./c1lib";
import { Section } from "./PublicLayout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Landmark, ArrowRight, CheckCircle2 } from "lucide-react";

export default function DevoteeJourney() {
  const [temples, setTemples] = useState([]);
  const [temple, setTemple] = useState(null);
  const [sevas, setSevas] = useState([]);
  const [seva, setSeva] = useState(null);
  const [fields, setFields] = useState({});
  const [dakshina, setDakshina] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const isLive = !!(temple && temple.is_live);

  useEffect(() => {
    demo.temples().then((rows) => {
      setTemples(rows || []);
      // LIVE first per backend ordering; default to first LIVE, else first row.
      const first = (rows || []).find((t) => t.is_live) || (rows || [])[0] || null;
      setTemple(first);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!temple) return;
    setSeva(null);
    setFields({});
    setDakshina("");
    demo.sevas(temple.temple_id).then(setSevas).catch(() => setSevas([]));
  }, [temple]);

  const liveTemples = useMemo(() => temples.filter((t) => t.is_live), [temples]);
  const demoTemples = useMemo(() => temples.filter((t) => !t.is_live), [temples]);

  const pick = (s) => { setSeva(s); setFields({}); setDakshina(""); setDone(null); };
  const set = (k, v) => setFields((f) => ({ ...f, [k]: v }));

  const fin = seva ? previewFinancial(seva.temple_amount, Number(dakshina || 0), isLive) : null;

  const submit = async () => {
    setBusy(true);
    try {
      const r = await demo.create({ seva_id: seva.seva_id, dakshina: Number(dakshina || 0), sankalpa: fields });
      setDone({ ...r, live: isLive });
      toast.success(`Sankalpa created. Record ${r.record_id} — status BOOKED (payment ≠ fulfilment).`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create Sankalpa");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <Section title="Sankalpa Created">
        <div className="max-w-xl bg-white border border-slate-200 rounded-sm p-8" data-testid="devotee-done">
          {done.live ? <LiveTag label="LIVE TEMPLE FLOW · TEST JOURNEY" /> : <SyntheticTag />}
          <CheckCircle2 size={28} className="text-emerald-600 mt-4" />
          <h2 className="font-serif text-3xl text-slate-950 mt-3">Your Pramaana record is open</h2>
          <p className="text-sm text-slate-600 mt-2">Status is <strong>BOOKED</strong>. A successful payment proves payment only — it is not yet evidence that the seva was performed. Certification depends on temple fulfilment, evidence, authorised validation, and the written PR-O003 minimum evidence standard.</p>
          <div className="font-mono text-sm text-slate-700 bg-slate-100 rounded-sm px-3 py-2 mt-4 inline-block" data-testid="new-record-id">{done.record_id}</div>
          <div className="flex gap-3 mt-6">
            <Link to={`/records/${done.record_id}`} data-testid="track-record"><Button className="rounded-sm bg-slate-900 hover:bg-slate-800 gap-2">Track this record <ArrowRight size={15} /></Button></Link>
            <Link to="/temple-manager"><Button variant="outline" className="rounded-sm">Continue as Temple Manager</Button></Link>
          </div>
        </div>
      </Section>
    );
  }

  return (
    <Section
      title="Devotee Journey"
      subtitle="Choose a verified temple and temple-approved Seva, then make your Sankalpa. Pramaana keeps the temple-declared amount, fulfilment evidence and resulting Verified Record traceable. Synthetic demonstration options remain available for testing."
    >
      {/* Temple picker: LIVE first, then Demonstration / Reference */}
      <div className="mb-8 space-y-6" data-testid="temple-picker">
        {liveTemples.length > 0 && (
          <div data-testid="live-temples-section">
            <h3 className="font-mono text-[11px] uppercase tracking-wider text-emerald-800 mb-3">Live · Verified Temples</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {liveTemples.map((t) => (
                <TempleCard key={t.temple_id} t={t} live selected={temple?.temple_id === t.temple_id} onSelect={() => setTemple(t)} />
              ))}
            </div>
          </div>
        )}
        {demoTemples.length > 0 && (
          <div data-testid="demo-temples-section">
            <h3 className="font-mono text-[11px] uppercase tracking-wider text-slate-500 mb-3">Demonstration / Reference</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {demoTemples.map((t) => (
                <TempleCard key={t.temple_id} t={t} live={false} selected={temple?.temple_id === t.temple_id} onSelect={() => setTemple(t)} />
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5">
          <h3 className="font-mono text-[11px] uppercase tracking-wider text-slate-500 mb-3">Temple-approved sevas</h3>
          <div className="space-y-3" data-testid="seva-list">
            {sevas.length === 0 && (
              <div className="border border-dashed border-slate-300 rounded-sm p-6 text-center text-slate-400 text-sm">No sevas available for this temple.</div>
            )}
            {sevas.map((s) => (
              <button
                key={s.seva_id}
                onClick={() => pick(s)}
                data-testid={`seva-${s.seva_id}`}
                className={`w-full text-left border rounded-sm p-4 transition-colors ${seva?.seva_id === s.seva_id ? (isLive ? "border-emerald-600 bg-emerald-50/40" : "border-slate-900 bg-slate-50") : "border-slate-200 bg-white hover:border-slate-400"}`}
              >
                <div className="flex items-center justify-between">
                  <div className="font-medium text-slate-900">{s.name}</div>
                  <div className="font-mono text-sm text-slate-700" data-testid={`seva-amount-${s.seva_id}`}>{money(s.temple_amount)}</div>
                </div>
                <p className="text-xs text-slate-500 mt-1">{s.description}</p>
                <div className="text-[10px] font-mono text-slate-400 mt-2">
                  {isLive
                    ? "Evidence standard: Temple receipt + temple-authorised fulfilment evidence"
                    : `Evidence required: ${(s.evidence_required || []).join(", ")}`}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="lg:col-span-7">
          {!seva ? (
            <div className="border border-dashed border-slate-300 rounded-sm p-10 text-center text-slate-400 text-sm">Select a seva to enter your Sankalpa.</div>
          ) : (
            <div className="bg-white border border-slate-200 rounded-sm p-6" data-testid="sankalpa-form">
              <div className="flex items-center gap-2 mb-3">
                {isLive
                  ? <LiveTag label="LIVE TEMPLE" />
                  : <SyntheticTag />}
              </div>
              <h3 className="font-serif text-2xl text-slate-950 mb-4">{seva.name} — Sankalpa</h3>
              <div className="space-y-4">
                {(seva.sankalpa_fields || []).map((f) => (
                  <div key={f.key}>
                    <Label className="font-mono text-[11px] uppercase tracking-wider text-slate-500">{f.label}{f.required && <span className="text-red-600"> *</span>}</Label>
                    {f.key === "intention"
                      ? <Textarea data-testid={`field-${f.key}`} value={fields[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} className="mt-1.5 rounded-sm border-slate-300 min-h-[70px]" />
                      : <Input data-testid={`field-${f.key}`} type={f.key === "requested_date" ? "date" : "text"} value={fields[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} className="mt-1.5 rounded-sm border-slate-300" />}
                  </div>
                ))}
                <div>
                  <Label className="font-mono text-[11px] uppercase tracking-wider text-slate-500">Optional Priest / Pandit Dakshina</Label>
                  <Input data-testid="field-dakshina" type="number" value={dakshina} onChange={(e) => setDakshina(e.target.value)} placeholder="0" className="mt-1.5 rounded-sm border-slate-300" />
                </div>
              </div>

              {fin && (
                <div className="mt-6 border border-slate-200 rounded-sm" data-testid="financial-preview">
                  <div className="flex items-center justify-between px-4 py-2 border-b border-slate-100 bg-slate-50">
                    <span className="font-mono text-[11px] uppercase tracking-wider text-slate-500">Transparent Financial Record</span>
                    {isLive
                      ? <span data-testid="financial-model-live" className="font-mono text-[9px] uppercase tracking-wider text-emerald-800 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-sm">LIVE · TEMPLE-DECLARED</span>
                      : <span data-testid="financial-model-demo" className="font-mono text-[9px] uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-sm">DEMO / WORKING MODEL</span>}
                  </div>
                  <table className="w-full text-sm px-4">
                    <tbody>
                      {fin.items.map((li) => (
                        <tr key={li.label} className="border-b border-slate-100">
                          <td className="py-2 pl-4 text-slate-700" data-testid={`fin-row-${li.key}`}>
                            {li.label}
                            <div className="text-[10px] text-slate-400">belongs to {li.who}</div>
                          </td>
                          <td className="py-2 pr-4 text-right font-mono text-slate-800" data-testid={`fin-amount-${li.key}`}>{money(li.amount)}</td>
                        </tr>
                      ))}
                      <tr>
                        <td className="py-2 pl-4 font-medium text-slate-900">Total</td>
                        <td className="py-2 pr-4 text-right font-mono font-semibold" data-testid="fin-total">{money(fin.total)}</td>
                      </tr>
                    </tbody>
                  </table>
                  <p className="text-[10px] text-slate-400 px-4 pb-3">
                    {isLive
                      ? "Temple-declared amount belongs to the temple. No Pramaana service fee on this live record. Payment proves payment — not fulfilment."
                      : "15% Pramaana fee is illustrative only, not final pricing. Payment proves payment — not fulfilment."}
                  </p>
                </div>
              )}

              <Button onClick={submit} disabled={busy} data-testid="submit-sankalpa" className={`w-full mt-6 rounded-sm ${isLive ? "bg-emerald-700 hover:bg-emerald-800" : "bg-slate-900 hover:bg-slate-800"}`}>
                {busy ? "Creating…" : (isLive ? "Confirm Sankalpa" : "Confirm Sankalpa & Synthetic Payment")}
              </Button>
              <p className="text-[10px] text-slate-400 mt-3">
                Creating this Sankalpa opens a new record with status <strong>BOOKED</strong>. It does not certify the seva. Certification only follows temple fulfilment, evidence, authorised validation and the written PR-O003 minimum evidence standard.
              </p>
            </div>
          )}
        </div>
      </div>
    </Section>
  );
}

function TempleCard({ t, live, selected, onSelect }) {
  const base = "text-left w-full border rounded-sm p-4 transition-colors";
  const activeCls = selected
    ? (live ? "border-emerald-600 bg-emerald-50/50" : "border-slate-900 bg-slate-50")
    : "border-slate-200 bg-white hover:border-slate-400";
  return (
    <button
      onClick={onSelect}
      data-testid={`temple-card-${t.temple_id}`}
      data-live={live ? "true" : "false"}
      className={`${base} ${activeCls}`}
    >
      <div className="flex items-start gap-3">
        <Landmark size={18} className={live ? "text-emerald-700 mt-0.5" : "text-slate-700 mt-0.5"} />
        <div className="flex-1 min-w-0">
          <div className="font-medium text-slate-900 truncate">{t.name}</div>
          {t.location && <div className="text-xs text-slate-500">{t.location}</div>}
          <div className="text-[10px] font-mono text-slate-400 mt-1">
            {t.temple_id} · {t.verification_id || "—"} · {(t.verification_status || "").replace("_", " — ")}
          </div>
        </div>
        {live
          ? <LiveTag label="LIVE · VERIFIED" />
          : <SyntheticTag />}
      </div>
    </button>
  );
}

// UI-side preview mirrors backend financial model.
// LIVE: temple + optional dakshina, no fee/tax.
// DEMO: 15% Pramaana fee + 18% demonstration tax on the fee.
function previewFinancial(temple, dakshina, isLive) {
  if (isLive) {
    return {
      items: [
        { key: "temple_seva_amount", label: "Temple Seva Amount", amount: temple, who: "Temple" },
        { key: "dakshina", label: "Priest / Pandit Dakshina", amount: dakshina, who: "Priest" },
        { key: "pramaana_fee", label: "Pramaana Service Fee", amount: 0, who: "Pramaana" },
      ],
      total: Math.round((temple + dakshina) * 100) / 100,
    };
  }
  const fee = Math.round(temple * 0.15 * 100) / 100;
  const tax = Math.round(fee * 0.18 * 100) / 100;
  const total = Math.round((temple + dakshina + fee + tax) * 100) / 100;
  return {
    items: [
      { key: "temple_seva_amount", label: "Temple Seva Amount", amount: temple, who: "Temple" },
      { key: "dakshina", label: "Priest / Pandit Dakshina", amount: dakshina, who: "Priest" },
      { key: "pramaana_fee", label: "Pramaana Service Fee (demo)", amount: fee, who: "Pramaana" },
      { key: "tax", label: "Applicable Tax (demo)", amount: tax, who: "Government" },
    ], total,
  };
}
